'use client'

import { formatDistanceToNowStrict } from 'date-fns'
import { Loader2Icon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { CardCornerGlow } from '@/components/widget/card-corner-glow'
import { ExchangeIcon } from '@/components/widget/exchange-icon'
import { accountRoleTranslationKey } from '@/features/account-access/lib/roles'
import { tradeVenueToIconKey } from '@/lib/proto/util'
import { toast } from '@/lib/toast'
import { cn } from '@/lib/utils'
import { formatFirstLetterToUpperCase } from '@/lib/utils/format-util'
import { useMyInvites, useRespondToInvite } from '@/service/account-access.hook'
import { MyInviteEntry } from '@/types/generated/public_api'

// Renders the logged-in user's pending account invites with Accept / Decline.
// There is no push channel for invites — useMyInvites polls while the tab is visible.
export function PendingInvites({
  className,
  onAccepted,
}: {
  className?: string
  onAccepted?: (accountId: string) => void
}) {
  const t = useTranslations('settings')
  const { data } = useMyInvites()
  const invites = data?.invites ?? []

  if (invites.length === 0) return null

  return (
    <section
      aria-label={t('pendingInvites')}
      aria-live="polite"
      className={cn('flex w-full flex-col gap-2', className)}
    >
      {invites.map((invite) => (
        <InviteCard key={invite.accountId} invite={invite} onAccepted={onAccepted} />
      ))}
    </section>
  )
}

function InviteCard({
  invite,
  onAccepted,
}: {
  invite: MyInviteEntry
  onAccepted?: (accountId: string) => void
}) {
  const t = useTranslations('settings')
  const [responding, setResponding] = useState<'accept' | 'decline' | null>(null)

  const { mutate: respond, isPending } = useRespondToInvite({
    onSuccess: (result, variables) => {
      toast.success(result.message)
      setResponding(null)
      if (variables.accept) {
        onAccepted?.(variables.accountId)
      }
    },
    onError: (error) => {
      toast.error(error.message || t('failedRespondInvite'))
      setResponding(null)
    },
  })

  const handleRespond = (accept: boolean) => {
    setResponding(accept ? 'accept' : 'decline')
    respond({ accountId: invite.accountId, accept })
  }

  const accountName = formatFirstLetterToUpperCase(invite.accountName)
  const inviteLabel = t('inviteTitle', { email: invite.inviterEmail, name: accountName })
  const roleLabel = t(`role.${accountRoleTranslationKey(invite.role)}`)
  const expiresLabel = t('expires', {
    time: formatDistanceToNowStrict(Number(invite.expiresAt), {
      addSuffix: true,
    }),
  })

  return (
    <div
      className={cn([
        'border-foreground/15 relative flex w-full flex-wrap items-center justify-between gap-x-4 gap-y-2 overflow-hidden border px-3 py-2.5',
        'to-ox-teal/10 bg-linear-to-r from-transparent',
        // An invite usually lands mid-session via polling: settle in instead of popping.
        'transition-[opacity,translate] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]',
        'starting:-translate-y-1 starting:opacity-0 motion-reduce:starting:translate-y-0',
      ])}
    >
      <CardCornerGlow />

      <div className="flex min-w-0 flex-1 items-center gap-2.5">
        <ExchangeIcon
          exchange={tradeVenueToIconKey(invite.tradeVenue)}
          className="size-5 shrink-0 rounded-full"
          logoObjectFit="contain"
        />
        <div className="flex min-w-0 flex-col gap-0.5">
          <p title={inviteLabel} className="text-foreground/70 truncate text-sm">
            <span className="text-foreground font-medium">{invite.inviterEmail}</span>{' '}
            {t('invitedYouTo', { name: accountName })}
          </p>
          <p className="text-foreground/50 truncate text-xs">
            {roleLabel}
            <span aria-hidden className="text-foreground/30 px-1.5">
              ·
            </span>
            {expiresLabel}
          </p>
        </div>
      </div>

      <div className="ml-auto flex shrink-0 items-center gap-1.5">
        <Button
          variant="subtle"
          size="sm"
          disabled={isPending}
          onClick={() => handleRespond(false)}
        >
          <PendingLabel pending={responding === 'decline'}>{t('decline')}</PendingLabel>
        </Button>
        <Button
          variant="default"
          size="sm"
          disabled={isPending}
          onClick={() => handleRespond(true)}
        >
          <PendingLabel pending={responding === 'accept'}>{t('accept')}</PendingLabel>
        </Button>
      </div>
    </div>
  )
}

// Keeps the label in layout while the spinner shows, so the button doesn't shrink mid-request.
function PendingLabel({ pending, children }: { pending: boolean; children: React.ReactNode }) {
  return (
    <span className="relative inline-grid place-items-center">
      <span className={cn(pending && 'invisible')}>{children}</span>
      {pending && <Loader2Icon aria-hidden className="absolute size-3.5 animate-spin" />}
    </span>
  )
}
